import { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "react-redux";
import { Account, UserSettings } from "../../_app/types";
import { HubError } from "../../class/Error";
import { showNotification } from "../../store/slices/notificationSlice";
import { RootState } from "../../store/store";
import { readNotification, setAccounts, switchAccount } from "../../store/slices/userSlice";
import { IconButtonPaper } from "../../_components/IconButtonPaper";
import { ButtonStyled } from "../../_components/ButtonStyled";
import IconComponent from "../../components/ui/IconComponent";
import Bell from "./assets/Bell";
import ChevronLeft from "./assets/ChevronLeft";
import AccountPopup from "./components/AccountPopup";
import { useApi } from "../../_hooks/use-api";
import { useAppNavigate } from "../../_hooks/use-app-navigate";

interface Props {
  pageTitle: string;
  needBack?: boolean;
  hideButtons?: boolean;
  hideAccountsButton?: boolean;
  onChange?: (value: boolean) => void;
  disabledBackButton?: boolean;
  toMainScreen?: boolean;
}

const Header = ({
  pageTitle,
  needBack,
  hideButtons,
  hideAccountsButton,
  onChange,
  disabledBackButton,
  toMainScreen,
}: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const api = useApi();
  const user = useSelector((state: RootState) => state.user);
  const navigate = useAppNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const handleBackClick = () => {
    if (toMainScreen) {
      navigate.bots();
    } else {
      if (onChange) onChange(false);
      else navigate.back();
    }
  };

  const handleOpen = () => setOpen((prevState) => !prevState);
  const handleClose = async () => setOpen(false);

  const handleSwitchAccount = async (value: Account) => {
    setIsSwitching(true);
    let settings: UserSettings[] = [
      { name: "ACTIVE_ACCOUNT_ID", value: value.id, format: "STRING" },
    ];
    try {
      await api.updateUserSettings(user.profile.profileId, settings);
      dispatch(
        showNotification({
          message: `Account ${value.name} switched`,
          severity: "info",
        })
      );
      dispatch(switchAccount(value));
      setOpen(false);

      navigate.bots();
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    } finally {
      setIsSwitching(false);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(readNotification());
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid`,
          borderColor:
            theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(21, 21, 21, 0.2)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "56px",
          paddingLeft: `${!needBack && "12px"}`,
        }}
      >
        {needBack && (
          <IconButtonPaper disabled={disabledBackButton} outlinedCustom onClick={handleBackClick}>
            <ChevronLeft size={18} />
          </IconButtonPaper>
        )}

        <Typography sx={{ marginRight: "auto" }} variant={"h3"} color="textPrimary">
          {pageTitle}
        </Typography>

        {!hideButtons && user.profile.accounts.length && (
          <>
            {!hideAccountsButton && (
              <Stack direction={"row"} alignItems={"center"}>
                <ButtonStyled
                  variant={"text"}
                  sx={{ display: "flex", alignItems: "center", gap: "3px" }}
                  onClick={handleOpen}
                >
                  {user.activeAccount.name}
                  <Box sx={{ height: "18px", rotate: `${open ? "180deg" : "0deg"}` }}>
                    <IconComponent iconName={"chevron-down"} hasStroke hasFill />
                  </Box>
                </ButtonStyled>
              </Stack>
            )}

            <Box sx={{ position: "relative" }}>
              {user.newNotification && (
                <Box
                  sx={{
                    width: 10,
                    top: 7,
                    right: 9,
                    zIndex: 1,
                    height: 10,
                    position: "absolute",
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              )}
              <IconButtonPaper outlinedCustom onClick={() => navigate.notifications()}>
                <Bell size={18} />
              </IconButtonPaper>
            </Box>
          </>
        )}
      </Box>

      <AccountPopup
        open={open}
        onClose={handleClose}
        accounts={user.profile.accounts}
        activeAccount={user.activeAccount.id}
        onChangeAccount={!isSwitching ? handleSwitchAccount : () => {}}
      />
    </>
  );
};

export default Header;
